import { Sneaker } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-12">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Sneaker size={24} weight="duotone" className="text-[#FF3B30]" />
          <span className="font-heading font-black text-lg tracking-tighter uppercase">SOLEFINDER</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors mb-8" data-testid="privacy-back-btn">
          <span>&larr;</span> Back to Home
        </Link>

        <h1 className="font-heading font-black text-3xl tracking-tighter uppercase mb-2" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-sm text-[#A1A1AA] mb-10">Last updated: April 2026</p>

        <div className="prose prose-sm max-w-none text-[#52525B] space-y-8">
          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">1. Introduction</h2>
            <p>SoleFinder ("we", "our", "us") operates the website solefinder.app (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Service. Please read this policy carefully. By using the Service, you consent to the data practices described in this policy.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">2. Information We Collect</h2>
            <h3 className="font-bold text-sm text-[#0A0A0A] mt-4">2.1 Personal Information</h3>
            <p>When you register for an account, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name and email address</li>
              <li>Account credentials (password is stored in encrypted/hashed form)</li>
              <li>Shoe size, foot measurements, and fit preferences (from the quiz)</li>
              <li>Wishlist and product comparison selections</li>
            </ul>

            <h3 className="font-bold text-sm text-[#0A0A0A] mt-4">2.2 Automatically Collected Information</h3>
            <p>When you access the Service, we may automatically collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Device and browser type, operating system</li>
              <li>IP address and approximate geographic location</li>
              <li>Pages visited, time spent, and interaction data</li>
              <li>Referral URLs and search terms</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="font-bold text-sm text-[#0A0A0A] mt-4">2.3 Information from Third Parties</h3>
            <p>We may receive information from affiliate networks and retail partners regarding purchases made through our links, including order confirmation and commission data. This data does not include your payment details.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, maintain, and improve the Service</li>
              <li>Personalise shoe recommendations based on your fit profile</li>
              <li>Send price drop alerts and notifications you have opted into</li>
              <li>Send promotional emails (with your consent; you can unsubscribe at any time)</li>
              <li>Process affiliate transactions and track commissions</li>
              <li>Analyse usage patterns to improve the Service</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">4. Sharing Your Information</h2>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Affiliate Networks & Retail Partners:</strong> When you click an outbound link to a retailer, the affiliate network may receive a cookie identifier. We do not share your name, email, or personal details with retailers.</li>
              <li><strong>Service Providers:</strong> Third-party companies that help us operate the Service (e.g., hosting, email delivery, analytics). These providers are contractually obligated to protect your data.</li>
              <li><strong>Legal Requirements:</strong> If required by law, regulation, or legal process.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">5. Cookies & Tracking Technologies</h2>
            <p>We use cookies and similar technologies for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Essential Cookies:</strong> Required for authentication, security, and basic functionality.</li>
              <li><strong>Preference Cookies:</strong> Remember your settings (e.g., currency, region).</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use the Service (e.g., Google Analytics).</li>
              <li><strong>Affiliate Cookies:</strong> Track referrals to retail partners for commission purposes. These are set by third-party affiliate networks when you click outbound links.</li>
            </ul>
            <p className="mt-2">You can control cookies through your browser settings. Disabling cookies may limit some features of the Service.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">6. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide the Service. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law or for legitimate business purposes (e.g., fraud prevention).</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">7. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data.</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format.</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time using the link in any email.</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at hello@solefinder.app.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">8. International Data Transfers</h2>
            <p>The Service operates across Australia, the United Kingdom, and the United States. Your data may be transferred to and processed in countries other than your own. We take appropriate safeguards to ensure your data is protected in accordance with applicable data protection laws, including GDPR (UK/EU) and the Australian Privacy Act 1988.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">9. Children's Privacy</h2>
            <p>The Service is not directed at children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">10. Security</h2>
            <p>We implement appropriate technical and organisational measures to protect your data, including encryption of data in transit (HTTPS/TLS), hashed password storage, and access controls. However, no method of transmission over the Internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">12. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p className="mt-2">
              <strong>SoleFinder</strong><br />
              Email: hello@solefinder.app<br />
              Website: solefinder.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
